import React, { useState, useEffect } from 'react';

import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { BasicModalWithFooter, Switch, Text } from '@hz-design-system/web-ui';

import getIsMobile from '@/client/utils/browser/isMobile';
import Classes from './SaveSearchModal.module.scss';

type SaveSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  handleGoToSavedSearchesPage: () => void;
  onEmailToggle: (emailEnabled: boolean) => Promise<boolean>;
  onPushToggle: (pushEnabled: boolean) => Promise<boolean>;
  initialEmailEnabled?: boolean;
  initialPushEnabled?: boolean;
};

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  isOpen,
  onClose,
  handleGoToSavedSearchesPage,
  onEmailToggle,
  onPushToggle,
  initialEmailEnabled = true,
  initialPushEnabled = true,
}) => {
  const { t } = useI18nContext();
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(initialEmailEnabled);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(initialPushEnabled);
  const [isMobile, setIsMobile] = useState(() => getIsMobile());
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    setIsMobile(getIsMobile());
  }, []);

  useEffect(() => {
    setEmailNotificationsEnabled(initialEmailEnabled);
    setPushNotificationsEnabled(initialPushEnabled);
  }, [initialEmailEnabled, initialPushEnabled]);

  if (!isMounted) {
    return null;
  }

  const handleEmailToggle = async () => {
    const newEmailState = !emailNotificationsEnabled;
    // Update the state immediately optimistically
    setEmailNotificationsEnabled(newEmailState);

    const success = await onEmailToggle(newEmailState);

    // Revert the toggle state if the API call failed
    if (!success) {
      setEmailNotificationsEnabled(!newEmailState);
    }
  };

  const handlePushToggle = async () => {
    const newPushState = !pushNotificationsEnabled;
    // Update the state immediately optimistically
    setPushNotificationsEnabled(newPushState);

    const success = await onPushToggle(newPushState);

    // Revert the toggle state if the API call failed
    if (!success) {
      setPushNotificationsEnabled(!newPushState);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <BasicModalWithFooter
      isOpen={isOpen}
      title={isMobile ? t('saveSearch.modal.titleShort') : t('saveSearch.modal.title')}
      size="m"
      isBottomSheet
      onClose={handleClose}
      buttons={[
        {
          component: 'SecondaryButton',
          text: isMobile ? t('saveSearch.buttonSavedShort') : t('saveSearch.buttonSavedLong'),
          onClick: handleGoToSavedSearchesPage,
        },
        {
          component: 'PrimaryButton',
          text: t('saveSearch.modal.closeButton'),
          onClick: handleClose,
        },
      ]}
    >
      <Text.Large strong className={Classes.description}>
        {t('saveSearch.modal.description')}
      </Text.Large>

      <Switch
        isOn={pushNotificationsEnabled}
        onChange={handlePushToggle}
        bodyTitle={t('saveSearch.modal.pushNotifications')}
      />
      <Switch
        isOn={emailNotificationsEnabled}
        onChange={handleEmailToggle}
        bodyTitle={t('saveSearch.modal.emailNotifications')}
      />
    </BasicModalWithFooter>
  );
};

export default SaveSearchModal;
